import { Access } from "@p0security/shared/types/access/index";

// Every access grants to the requestor's own Auth0 user, found by their P0
// email, and needs `tenant` (the lifecycle resolves the install context from
// it). `connection` picks the user when several, one per connection, have the
// email.

// The requestor picks it from the installed tenants. The backend's commands
// select a lone one: `isPreselect` can't be declared once for every customer.
const tenant = {
  description: "Auth0 tenant",
  label: "Tenant",
  type: "static",
} as const;

const connection = {
  // Slack shows the description as the placeholder, which it caps at 150
  // characters, so the detail goes in the hint.
  description: "The Auth0 connection of your user",
  hint: "Such as Username-Password-Authentication or google-oauth2. Needed only when more than one Auth0 user has your email.",
  label: "Connection",
  optional: true,
  type: "dynamic",
} as const;

// `normalize` sets this from the lookup by email, so the approver sees which
// Auth0 user the request is for. Stage looks again, and refuses if it finds
// another user by then.
const auth0UserIdParameter = {
  description: "The Auth0 user P0 found for your email when you asked",
  label: "Auth0 user ID when requested",
  type: "generated",
} as const;

// `normalize` sets this from the name the requestor gave, so requests and
// rules read by name while the connector acts on the id.
const roleIdParameter = {
  description: "The id of the tenant role",
  label: "Role ID",
  type: "generated",
} as const;

const auth0UserId = {
  description: "The Auth0 user P0 grants to",
  label: "Auth0 user ID",
  type: "string",
} as const;

/** A recorded yes or no, since a generated value is a string. */
export const YesNo = ["yes", "no"] as const;
export type YesNo = (typeof YesNo)[number];

export const Auth0Accesses = {
  role: Access({
    delegation: {},
    description: "A tenant role, assigned to your Auth0 user",
    generated: { auth0UserId },
    label: "Role",
    maturity: "beta",
    // Reads `role <tenant> <role> [--connection]`.
    parameters: {
      tenant,
      role: {
        description: "The tenant role to assign, by name",
        label: "Role",
        type: "dynamic",
      },
      connection,
      roleId: roleIdParameter,
      auth0UserId: auth0UserIdParameter,
    },
    render: { locator: "tenant", policy: "role" } as const,
  }),
  permission: Access({
    delegation: {},
    description: "A permission on an API, assigned to your Auth0 user",
    generated: { auth0UserId },
    label: "Permission",
    maturity: "beta",
    // Reads `permission <tenant> <api> <permission> [--connection]`.
    parameters: {
      tenant,
      api: {
        description: "The API, by its identifier",
        label: "API",
        type: "dynamic",
      },
      permission: {
        description: "A permission the API defines",
        label: "Permission",
        type: "dynamic",
      },
      connection,
      auth0UserId: auth0UserIdParameter,
    },
    render: {
      locator: "tenant",
      policy: "permission",
      resource: "api",
    } as const,
  }),
  organization: Access({
    delegation: {},
    description:
      "Membership of an Auth0 organization, optionally with a tenant role in it",
    // Stage records these after approval, so a revoke takes back only what P0
    // added.
    generated: {
      auth0UserId,
      wasMember: {
        choices: YesNo,
        description:
          "Whether you were already a member when the request was approved. P0 never removes a membership it didn't add.",
        label: "Already a member",
        type: "string",
      },
      priorRoleIds: {
        description:
          "Your roles in the organization when the request was approved, which P0 never removes",
        label: "Prior roles",
        multivalued: true,
        type: "string",
      },
    },
    label: "Organization",
    maturity: "beta",
    // Reads `organization <tenant> <organization> [--role] [--connection]`.
    parameters: {
      tenant,
      organization: {
        description: "The Auth0 organization, by name",
        label: "Organization",
        type: "dynamic",
      },
      role: {
        description: "A tenant role to assign in the organization, by name",
        label: "Role",
        optional: true,
        type: "dynamic",
      },
      connection,
      organizationId: {
        description: "The id of the organization",
        label: "Organization ID",
        type: "generated",
      },
      roleId: { ...roleIdParameter, optional: true },
      auth0UserId: auth0UserIdParameter,
    },
    render: {
      locator: "tenant",
      policy: "role",
      resource: "organization",
    } as const,
  }),
};

export type Auth0Accesses = typeof Auth0Accesses;
export type Auth0RoleAccess = Auth0Accesses["role"];
export type Auth0PermissionAccess = Auth0Accesses["permission"];
export type Auth0OrganizationAccess = Auth0Accesses["organization"];
